import React, { useState, useCallback, useEffect } from 'react';

import { categoriesSelectors } from 'app-components/flux/categories';
import { tracker } from 'app-components/utils/core';

import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { dateSelectors } from 'app-components/flux/date';
import { filtersActions, filtersSelectors } from 'app-components/flux/filters';
import { freeToSpendActions, freeToSpendSelectors } from 'app-components/flux/free-to-spend';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import { rootUiActions } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import plannedSpendingIcon from 'assets/images/spendingPlan/PlannedSpending.svg';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import AmountField from 'components/QuickenControls/AmountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import usePlannedCategoryChecker from 'hooks/usePlannedCategoryChecker';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { deletePlannedSpendingDialog } from '../DeletePlannedSpending/actions';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';

const useStyles = makeStyles()((theme) => ({
  dialog: {
    height: 555,
    width: 850,
    margin: 'auto',
  },
  catAndAmt: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  spacer: {
    width: 24,
  },
  widthOfTextField: {
    margin: 0,
    width: 250,
  },
  amountField: {
    margin: 0,
    flexGrow: 1,
    textAlign: 'left !important',
  },
  radio: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    paddingTop: 16,
    gap: theme.spacing(2),
  },
  dialogContent: {
    paddingTop: `${theme.spacing(5)}px !important`,
  },
  suggested: {
    padding: theme.spacing(1),
    borderRadius: 12,
    width: '100%',
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0,
    marginTop: theme.spacing(5),
  },
  show: {
    opacity: '1 !important',
  },
  iconCat: {
    height: 40,
    width: 0,
    visibility: 'hidden',
    '&.visible': {
      paddingRight: 12,
      width: 40,
      visibility: 'visible',
    },
  },
  formRoot: {
    display: 'flex',
    flexDirection: 'row',
  },
  plannedIcon: {
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(4),
    height: 139,
    width: 139,
  },
  divider: {
    //  paddingTop: theme.spacing(2),
  },
}));

const defaultPlanned = Object.freeze({ name: '', targetAmount: 0, recurring: false });

function EditPlannedSpending(props) {
  const { freeToSpendId, cardIndex, startDate, filterId, location } = props;
  const [suggestedCategories, setSuggestedCategories] = usePreferenceV2('suggestedPSICategoryKnownIds');

  const plannedItems = useSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemsByF2SId(state, freeToSpendId),
  );
  const filter = useSelector((state) => filtersSelectors.getFilter(state, filterId));

  const { classes, cx } = useStyles();
  const freeToSpend = useSelector((state) => freeToSpendSelectors.getFreeToSpendById(state, freeToSpendId));
  const leftToSpend = useSelector((state) => freeToSpendSelectors.getLeftToSpend(state, freeToSpend));
  const parentCoaId = useSelector((state) => categoriesSelectors.getParentFromCoaList(state, filter));
  const doAllItemsExist = useSelector((state) =>
    freeToSpendSelectors.doPlannedSpendingItemsForDateAllHaveIds(state, startDate),
  );
  const calculatedPlannedAmount = useSelector((state) =>
    freeToSpendSelectors.getCalculatedPlannedSpending(state, freeToSpendId),
  );
  const currentDate = useSelector(dateSelectors.getCurrentDate);

  const plannedItem = plannedItems?.get(cardIndex) || defaultPlanned;
  const realTargetAmount =
    plannedItem.overwrittenTargetAmount === 0 ? 0 : plannedItem.overwrittenTargetAmount || plannedItem.targetAmount;
  const { name, recurring } = plannedItem;
  const [nameState, setName] = useState(name);
  const [targetAmountState, setTarget] = useState(realTargetAmount);
  const [editType, setEditType] = useState(recurring ? 'monthly' : 'onetime');
  // eslint-disable-next-line no-unused-vars
  const [validate, coa, coaSetter, errorText, currCategory, category, _isAlreadyTracked, children] =
    usePlannedCategoryChecker({
      id: parentCoaId,
      startDate,
      isEdit: true,
      isOneTime: editType === 'onetime',
    });
  const hasRecurringChanged = recurring ? editType === 'onetime' : editType === 'monthly';
  const isDisabled =
    realTargetAmount === targetAmountState && name === nameState && coa?.id === category?.id && !hasRecurringChanged;
  const isDisabledOrError = isDisabled || !!errorText;

  // Average spending checks
  const pastCutoff = currentDate.startOf('month').minus({ months: 6 }).toSeconds();
  const currCutoff = currentDate.startOf('month').minus({ months: 1 }).toSeconds();
  const { averageSpending } = useSelector((state) =>
    freeToSpendSelectors.getAverageSpendingForCategory(state, { coaId: coa?.id, pastCutoff, currCutoff }),
  );

  useEffect(() => {
    setName(name);
    setTarget(realTargetAmount);
    setEditType(recurring ? 'monthly' : 'onetime');
  }, [name, realTargetAmount, recurring]);

  useEffect(() => {
    validate(coa, plannedItem); // whenever edit type is changed, we call the setter to refresh error text
  }, [editType, coa, plannedItem, validate]);

  const dispatch = useDispatch();

  const removeCategoryFromSuggestedList = () => {
    if (suggestedCategories && setSuggestedCategories && suggestedCategories?.includes(currCategory?.knownCategoryId)) {
      setSuggestedCategories(suggestedCategories?.filter((item) => item !== currCategory?.knownCategoryId));
    }
  };

  const updateF2S = () => {
    const changed = [];
    if ((targetAmountState !== 0 && !targetAmountState) || targetAmountState < 0 || !coa.id) {
      return;
    }
    const item = plannedItems.get(cardIndex);
    if (!item) {
      return;
    }
    if (nameState !== item.name) {
      changed.push('name');
      item.name = nameState;
    }
    if (coa?.id !== category?.id) {
      changed.push('category');
      dispatch(
        filtersActions.updateFilter({
          id: filterId,
          filterItems: [
            {
              type: 'CHARTOFACCOUNT',
              chartOfAccounts: [coa?.toJS?.() || coa, ...(children?.map(({ id }) => ({ id, type: 'CATEGORY' })) ?? [])],
            },
          ],
        }),
      );
    }

    if (targetAmountState !== realTargetAmount) {
      changed.push('amount');
      if (item.overwrittenTargetAmount || item.overwrittenTargetAmount === 0) {
        item.resetOverwrittenTargetAmount = true;
        if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) delete item.overwrittenTargetAmount;
      }
      item.targetAmount = targetAmountState;
    }
    if (hasRecurringChanged) {
      changed.push('frequency');
      item.recurring = !recurring;
      if (editType !== 'monthly') {
        item.resetOverwrittenTargetAmount = true;
        item.targetAmount = item.overwrittenTargetAmount;
        if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) delete item.overwrittenTargetAmount;
      }
    }
    tracker.track(tracker.events.budgetItemEdit, {
      location,
      fields: changed,
    });
    const spendingItems = plannedItems.splice(cardIndex, 1, item); // TODO: change this to rely on first finding the item

    let total = 0;
    spendingItems?.forEach((plannedValue) => {
      const spentAmount = -1 * plannedValue.calculatedSpentAmount;
      if (
        (plannedValue.overwrittenTargetAmount === 0 || plannedValue.overwrittenTargetAmount) &&
        plannedValue.overwrittenTargetAmount >= spentAmount
      ) {
        total += plannedValue.overwrittenTargetAmount;
      } else if (plannedValue.targetAmount > spentAmount) {
        total += plannedValue.targetAmount;
      } else {
        total += spentAmount;
      }
    });
    const leftToSpendAmount = leftToSpend - calculatedPlannedAmount - total;

    dispatch(
      freeToSpendActions.updateFreeToSpend({
        id: freeToSpendId,
        plannedSpendingItems: spendingItems,
        calculatedPlannedSpendingAmount: -1 * total,
        leftToSpendAmount,
      }),
    );
    removeCategoryFromSuggestedList();
  };

  const setPlannedName = useCallback((e) => {
    setName(e.target.value);
  }, []);

  const onHandleClose = (event, reason) => {
    if (typeof event?.stopPropagation === 'function') {
      event?.stopPropagation();
    }
    if (typeof event?.preventDefault === 'function') {
      event?.preventDefault();
    }
    if (reason === 'SUBMIT_BUTTON' || reason === 'ENTER_KEY_DOWN') {
      // update
      updateF2S();
    }
    dispatch(rootUiActions.removeDialog());
  };

  const filterFn = (c) =>
    c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE ||
    (c.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isExpenseCat(null, c.id)) ||
    c.type === chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED; // only expense categories or uncat

  const dateLabel = `${DateTime.fromISO(startDate).toFormat('LLL yyyy')} only`;
  const showAverageSpend = coa.id && Math.abs(averageSpending || 0) > 0;

  const formatAmount = useFormatAmount();

  const formatNumber = (value) =>
    formatAmount(parseFloat(Math.abs(value || 0)), 'USD', '0,0.00', numberUtils.ShowSignEnum.NEGATIVE_ONLY);

  return (
    <StdDialog
      classes={{ root: classes.dialog }}
      onClose={onHandleClose}
      onKeyDown={(e) => {
        if (e.code === 'Enter' && !isDisabledOrError) {
          // editHide();
          onHandleClose(e, 'ENTER_KEY_DOWN');
        }
      }}
      open
      showCloseButton
      size='md'
      title='Edit planned expense'
    >
      <DialogContent className={classes.dialogContent}>
        <div className={classes.formRoot}>
          <Box height={109} minWidth={109} alignItems={'center'} justifyContent={'center'} display={'flex'}>
            <img src={plannedSpendingIcon} alt={'planned-icon'} className={classes.plannedIcon} />
          </Box>
          <Stack spacing={3} maxWidth={'500px'}>
            <Typography variant='bodyRegular' component={'span'}>
              {'Make any changes to a one-time expense.'}
            </Typography>
            <div className={classes.catAndAmt}>
              <TextField
                margin='normal'
                id='name'
                label={'Name (optional)'}
                inputProps={{ maxLength: 30 }}
                variant='filled'
                value={nameState}
                onChange={setPlannedName}
                className={classes.widthOfTextField}
              />
              <div className={classes.spacer} />
              <AmountField
                editable
                id='target-amount'
                variant={'filled'}
                label={'Target amount'}
                marginProp={'normal'}
                value={targetAmountState}
                onChange={(e) => setTarget(Number(e.target.value))}
                textFieldVariant={'filled'}
                className={classes.amountField}
              />
            </div>
            <CategoryField
              variant='bodyRegular'
              id='monthly'
              editable
              label='Select category'
              textFieldVariant='filled'
              filterFn={filterFn}
              value={coa}
              onChange={(c) => coaSetter(c, plannedItem)}
              width='100%'
              margin='none'
              error={!!errorText}
              helperText={errorText}
              textFieldProps={{
                label: 'Category',
                variant: 'filled',
                margin: 'none',
              }}
            />
            <div className={cx(classes.suggested, showAverageSpend && classes.show)}>
              {
                <img
                  alt='cat-icon'
                  src={currCategory?.logoUrl || ''}
                  className={cx(classes.iconCat, currCategory?.logoUrl ? 'visible' : '')}
                />
              }
              <Typography variant='paragraphRegular'>
                {`Average spend in this category each month: ${formatNumber(averageSpending)}`}
              </Typography>
            </div>
            <Divider className={classes.divider} />
            <RadioGroup
              aria-label='quiz'
              name='quiz'
              value={editType}
              className={classes.radio}
              onChange={(e) => setEditType(e.target.value)}
            >
              <FormControlLabel value='onetime' control={<Radio id={'edit-one-time-button'} />} label={dateLabel} />
              <FormControlLabel
                value='monthly'
                control={<Radio id={'edit-monthly-button'} />}
                label='Recurring expense'
              />
            </RadioGroup>
          </Stack>
        </div>
      </DialogContent>
      <DialogActions>
        <QButton
          disabled={!doAllItemsExist}
          variant='delete'
          type='submit'
          id={'edit-delete-button'}
          onClick={() => {
            dispatch(rootUiActions.removeDialog());
            dispatch(deletePlannedSpendingDialog(plannedItem.filterId, freeToSpendId));
          }}
        >
          DELETE
        </QButton>
        <QButton
          variant='contained'
          type='submit'
          id={'update-button'}
          onClick={(e) => {
            onHandleClose(e, 'SUBMIT_BUTTON');
          }}
          disabled={isDisabledOrError}
        >
          UPDATE
        </QButton>
      </DialogActions>
    </StdDialog>
  );
}

EditPlannedSpending.propTypes = {
  cardIndex: PropTypes.number,
  freeToSpendId: PropTypes.string,
  startDate: PropTypes.string,
  filterId: PropTypes.string,
  location: PropTypes.string,
};

export default React.memo(EditPlannedSpending);
